import { useRef, useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence, type Variants } from 'motion/react';
import {
  Stethoscope,
  Users,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  CheckCircle2,
  ShieldAlert,
  Mail,
  LogOut,
  BarChart3,
  BookOpen,
  MessageSquare,
} from "lucide-react";
import { useReducedMotion } from "@/components/scrollytelling";
import { FloatingNav, SlideDownArrow } from "@/components/layout";
import { useRBAC } from "@/hooks/useRBAC";
import { useAuth } from "@/context/AuthContext";
import { PHYSICIAN_TRACK_ROLES, APC_TRACK_ROLES, isEquipReadOnly, isEquipCSSOnly } from "@/lib/roles";

// Permission types for role-based rendering
interface TrackPermissions {
  canAccessPhysician: boolean;
  canAccessAPC: boolean;
  hasAnyAccess: boolean;
  isAdmin: boolean;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const TOTAL_SLIDES = 6; // Hero + Outcomes Success + PIMs Data + Problem + Why + Close

// Animation variants
const contentVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay, ease: "easeOut" },
  }),
};

const slideLeftVariants: Variants = {
  hidden: { opacity: 0, x: 30 },
  visible: (delay: number = 0) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, delay, ease: "easeOut" },
  }),
};

// ============================================================================
// NAVIGATION COMPONENTS
// ============================================================================

interface SlideNavProps {
  currentSlide: number;
  totalSlides: number;
  onNext: () => void;
  onPrev: () => void;
  onJumpTo: (index: number) => void;
  isAnimating: boolean;
}

function SlideNavigation({
  currentSlide,
  totalSlides,
  onNext,
  onPrev,
  onJumpTo,
  isAnimating,
}: SlideNavProps) {
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === totalSlides - 1;

  return (
    <div className="fixed right-4 top-1/2 -translate-y-1/2 z-50 flex flex-col items-center gap-2 bg-eventus-navy/90 backdrop-blur-md rounded-full py-3 px-2 shadow-lg border border-white/10">
      <button
        onClick={onPrev}
        disabled={isFirst || isAnimating}
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
          isFirst || isAnimating
            ? "text-white/30 cursor-not-allowed"
            : "text-white hover:bg-white/20 hover:scale-110"
        }`}
        aria-label="Previous slide"
      >
        <ChevronUp className="w-5 h-5" />
      </button>

      <div className="flex flex-col gap-2 py-1">
        {Array.from({ length: totalSlides }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => !isAnimating && onJumpTo(idx)}
            disabled={isAnimating}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer hover:scale-150 ${
              idx === currentSlide
                ? "bg-eventus-teal scale-125"
                : "bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>

      <button
        onClick={onNext}
        disabled={isLast || isAnimating}
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
          isLast || isAnimating
            ? "text-white/30 cursor-not-allowed"
            : "text-white hover:bg-white/20 hover:scale-110"
        }`}
        aria-label="Next slide"
      >
        <ChevronDown className="w-5 h-5" />
      </button>
    </div>
  );
}

function HeroDownArrow({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.5, duration: 0.8 }}
      className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/80 hover:text-white transition-colors cursor-pointer"
    >
      <span className="text-xs tracking-widest uppercase">
        Scroll to continue
      </span>
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
      >
        <ChevronDown className="w-6 h-6" />
      </motion.div>
    </motion.button>
  );
}

// ============================================================================
// ANIMATED COUNTER
// ============================================================================

interface AnimatedCounterProps {
  from: number;
  to: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  isActive: boolean;
}

function AnimatedCounter({
  from,
  to,
  duration = 1500,
  prefix = "",
  suffix = "",
  className = "",
  isActive,
}: AnimatedCounterProps) {
  const [value, setValue] = useState(from);

  useEffect(() => {
    if (!isActive) {
      setValue(from);
      return;
    }

    const startTime = Date.now();
    let rafId: number;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(from + (to - from) * eased);

      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };

    const timeout = setTimeout(() => {
      rafId = requestAnimationFrame(animate);
    }, 400);

    return () => {
      clearTimeout(timeout);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [from, to, duration, isActive]);

  return (
    <span className={className}>
      {prefix}
      {Math.round(value).toLocaleString()}
      {suffix}
    </span>
  );
}

// ============================================================================
// OUTCOMES CHART - Two Line Charts (Medications + PIMs)
// ============================================================================

function OutcomesChart({ isActive }: { isActive: boolean }) {
  const reducedMotion = useReducedMotion();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setProgress(0);
      return;
    }
    const timeout = setTimeout(() => setProgress(1), 300);
    return () => clearTimeout(timeout);
  }, [isActive]);

  // Monthly data from pims_monthly_trends.csv (Oct 2024 - Dec 2025)
  const monthlyData = [
    {
      month: "Oct '24",
      opioid: 30.9,
      benzo: 21.1,
      antipsych: 25.2,
      pims: 3.96,
    },
    { month: "Nov", opioid: 31.4, benzo: 21.1, antipsych: 25.1, pims: 4.05 },
    { month: "Dec", opioid: 30.6, benzo: 20.8, antipsych: 26.0, pims: 3.39 },
    {
      month: "Jan '25",
      opioid: 30.4,
      benzo: 20.3,
      antipsych: 25.6,
      pims: 3.22,
    },
    { month: "Feb", opioid: 31.1, benzo: 20.3, antipsych: 25.7, pims: 3.5 },
    { month: "Mar", opioid: 31.1, benzo: 20.2, antipsych: 25.6, pims: 3.54 },
    { month: "Apr", opioid: 30.7, benzo: 20.1, antipsych: 25.7, pims: 3.42 },
    { month: "May", opioid: 30.3, benzo: 20.0, antipsych: 26.1, pims: 3.1 },
    { month: "Jun", opioid: 30.2, benzo: 20.1, antipsych: 25.8, pims: 2.92 },
    { month: "Jul", opioid: 29.0, benzo: 19.2, antipsych: 24.6, pims: 2.69 },
    { month: "Aug", opioid: 29.3, benzo: 19.1, antipsych: 24.2, pims: 2.68 },
    { month: "Sep", opioid: 27.9, benzo: 18.5, antipsych: 24.1, pims: 2.66 },
    { month: "Oct", opioid: 26.3, benzo: 16.9, antipsych: 22.1, pims: 2.78 },
    { month: "Nov", opioid: 26.2, benzo: 17.1, antipsych: 22.3, pims: 2.77 },
    { month: "Dec '25", opioid: 26.0, benzo: 16.7, antipsych: 22.2, pims: 2.8 },
  ];

  // % improvements (Oct 2024 to Dec 2025)
  const improvements = {
    opioid: 16,
    benzo: 21,
    antipsych: 12,
    pims: 29,
  };

  const colors = {
    opioid: "#053252",
    benzo: "#6bc4d6",
    antipsych: "#f97316",
    pims: "#877be9",
  };

  const drawProgress = reducedMotion ? 1 : progress;
  const pathLength = 600;

  return (
    <div className="w-full space-y-4">
      {/* Chart 1: Medication Rates */}
      <MedicationChart
        data={monthlyData}
        colors={colors}
        improvements={improvements}
        drawProgress={drawProgress}
        pathLength={pathLength}
      />

      {/* Chart 2: PIMs Score */}
      <PimsScoreChart
        data={monthlyData}
        color={colors.pims}
        improvement={improvements.pims}
        drawProgress={drawProgress}
        pathLength={pathLength}
      />
    </div>
  );
}

function MedicationChart({
  data,
  colors,
  improvements,
  drawProgress,
  pathLength,
}: {
  data: Array<{
    month: string;
    opioid: number;
    benzo: number;
    antipsych: number;
  }>;
  colors: { opioid: string; benzo: string; antipsych: string };
  improvements: { opioid: number; benzo: number; antipsych: number };
  drawProgress: number;
  pathLength: number;
}) {
  // Dynamic badge sizing based on label length
  const metrics = [
    {
      key: "opioid",
      label: "Opioids",
      color: colors.opioid,
      improvement: improvements.opioid,
      endValue: data[data.length - 1].opioid,
    },
    {
      key: "antipsych",
      label: "Antipsychotics",
      color: colors.antipsych,
      improvement: improvements.antipsych,
      endValue: data[data.length - 1].antipsych,
    },
    {
      key: "benzo",
      label: "Benzos",
      color: colors.benzo,
      improvement: improvements.benzo,
      endValue: data[data.length - 1].benzo,
    },
  ].map((m) => ({
    ...m,
    // ~5.5px per char for label + 38px for "↓XX%" + padding (less for longer labels)
    badgeWidth: Math.ceil(m.label.length * 5.5) + 38 + (m.label.length > 10 ? 12 : 20),
  }));

  const maxBadgeWidth = Math.max(...metrics.map((m) => m.badgeWidth));
  const badgeOffset = 14; // gap between line end and badge

  const chartHeight = 140;
  const paddingLeft = 36;
  const paddingRight = maxBadgeWidth + badgeOffset + 8;
  const paddingTop = 16;
  const paddingBottom = 24;
  const chartWidth = 420 + paddingRight; // fixed chart area + dynamic right padding

  const xMin = paddingLeft;
  const xMax = chartWidth - paddingRight;
  const yMin = paddingTop;
  const yMax = chartHeight - paddingBottom;

  // Y-axis range for medication percentages
  const dataMin = 14;
  const dataMax = 34;

  const getX = (index: number) =>
    xMin + (index / (data.length - 1)) * (xMax - xMin);
  const getY = (value: number) =>
    yMax - ((value - dataMin) / (dataMax - dataMin)) * (yMax - yMin);

  const getSmoothPath = (values: number[]) => {
    if (values.length < 2) return "";
    let path = `M ${getX(0)} ${getY(values[0])}`;
    for (let i = 1; i < values.length; i++) {
      const x0 = getX(i - 1),
        x1 = getX(i);
      const y0 = getY(values[i - 1]),
        y1 = getY(values[i]);
      const cpx = (x0 + x1) / 2;
      path += ` C ${cpx} ${y0}, ${cpx} ${y1}, ${x1} ${y1}`;
    }
    return path;
  };

  const showCallouts = drawProgress > 0.85;

  return (
    <div className="bg-slate-50/50 rounded-lg p-2">
      <div className="text-[10px] font-semibold text-slate-500 mb-1 px-1">
        High-Risk Medication Rates
      </div>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="w-full h-auto"
      >
        {/* Grid lines */}
        {[15, 20, 25, 30].map((value) => (
          <g key={value}>
            <line
              x1={xMin}
              y1={getY(value)}
              x2={xMax}
              y2={getY(value)}
              stroke="#e2e8f0"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
            <text
              x={xMin - 6}
              y={getY(value) + 3}
              className="text-[9px] fill-slate-400"
              textAnchor="end"
            >
              {value}%
            </text>
          </g>
        ))}

        {/* X-axis labels */}
        {data.map(
          (d, idx) =>
            (idx === 0 ||
              idx === 3 ||
              idx === 6 ||
              idx === 9 ||
              idx === 12 ||
              idx === 14) && (
              <text
                key={idx}
                x={getX(idx)}
                y={chartHeight - 6}
                className="text-[8px] fill-slate-400"
                textAnchor="middle"
              >
                {d.month}
              </text>
            ),
        )}

        {/* Lines */}
        <motion.path
          d={getSmoothPath(data.map((d) => d.opioid))}
          fill="none"
          stroke={colors.opioid}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={pathLength}
          initial={{ strokeDashoffset: pathLength }}
          animate={{ strokeDashoffset: pathLength * (1 - drawProgress) }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.1 }}
        />
        <motion.path
          d={getSmoothPath(data.map((d) => d.antipsych))}
          fill="none"
          stroke={colors.antipsych}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={pathLength}
          initial={{ strokeDashoffset: pathLength }}
          animate={{ strokeDashoffset: pathLength * (1 - drawProgress) }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
        />
        <motion.path
          d={getSmoothPath(data.map((d) => d.benzo))}
          fill="none"
          stroke={colors.benzo}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={pathLength}
          initial={{ strokeDashoffset: pathLength }}
          animate={{ strokeDashoffset: pathLength * (1 - drawProgress) }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
        />

        {/* End callouts with dynamic widths */}
        <AnimatePresence>
          {showCallouts &&
            metrics.map((m, idx) => {
              const endX = getX(data.length - 1);
              const endY = getY(m.endValue);
              return (
                <motion.g
                  key={m.key}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.3 }}
                >
                  <circle
                    cx={endX}
                    cy={endY}
                    r="4"
                    fill={m.color}
                    stroke="white"
                    strokeWidth="2"
                  />
                  <line
                    x1={endX + 4}
                    y1={endY}
                    x2={endX + 12}
                    y2={endY}
                    stroke={m.color}
                    strokeWidth="1.5"
                  />
                  <rect
                    x={endX + badgeOffset}
                    y={endY - 11}
                    width={m.badgeWidth}
                    height={22}
                    rx={11}
                    fill={m.color}
                  />
                  <text
                    x={endX + badgeOffset + 10}
                    y={endY + 4}
                    className="text-[11px] font-bold"
                    fill="white"
                  >
                    ↓{m.improvement}%
                  </text>
                  <text
                    x={endX + badgeOffset + 46}
                    y={endY + 4}
                    className="text-[10px]"
                    fill="rgba(255,255,255,0.9)"
                  >
                    {m.label}
                  </text>
                </motion.g>
              );
            })}
        </AnimatePresence>
      </svg>
    </div>
  );
}

function PimsScoreChart({
  data,
  color,
  improvement,
  drawProgress,
  pathLength,
}: {
  data: Array<{ month: string; pims: number }>;
  color: string;
  improvement: number;
  drawProgress: number;
  pathLength: number;
}) {
  // Dynamic badge sizing
  const label = "PIMs";
  const badgeWidth = Math.ceil(label.length * 5.5) + 38 + 20; // ~5.5px per char + "↓XX%" + padding
  const badgeOffset = 14;

  const chartHeight = 100;
  const paddingLeft = 36;
  const paddingRight = badgeWidth + badgeOffset + 8;
  const paddingTop = 16;
  const paddingBottom = 24;
  const chartWidth = 420 + paddingRight;

  const xMin = paddingLeft;
  const xMax = chartWidth - paddingRight;
  const yMin = paddingTop;
  const yMax = chartHeight - paddingBottom;

  // Y-axis range for PIMs score
  const dataMin = 2.4;
  const dataMax = 4.2;

  const getX = (index: number) =>
    xMin + (index / (data.length - 1)) * (xMax - xMin);
  const getY = (value: number) =>
    yMax - ((value - dataMin) / (dataMax - dataMin)) * (yMax - yMin);

  const getSmoothPath = (values: number[]) => {
    if (values.length < 2) return "";
    let path = `M ${getX(0)} ${getY(values[0])}`;
    for (let i = 1; i < values.length; i++) {
      const x0 = getX(i - 1),
        x1 = getX(i);
      const y0 = getY(values[i - 1]),
        y1 = getY(values[i]);
      const cpx = (x0 + x1) / 2;
      path += ` C ${cpx} ${y0}, ${cpx} ${y1}, ${x1} ${y1}`;
    }
    return path;
  };

  const showCallouts = drawProgress > 0.85;
  const endValue = data[data.length - 1].pims;

  return (
    <div className="bg-slate-50/50 rounded-lg p-2">
      <div className="text-[10px] font-semibold text-slate-500 mb-1 px-1">
        Avg Patient PIMs Score (lower is better)
      </div>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="w-full h-auto"
      >
        {/* Grid lines */}
        {[2.5, 3.0, 3.5, 4.0].map((value) => (
          <g key={value}>
            <line
              x1={xMin}
              y1={getY(value)}
              x2={xMax}
              y2={getY(value)}
              stroke="#e2e8f0"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
            <text
              x={xMin - 6}
              y={getY(value) + 3}
              className="text-[9px] fill-slate-400"
              textAnchor="end"
            >
              {value.toFixed(1)}
            </text>
          </g>
        ))}

        {/* X-axis labels */}
        {data.map(
          (d, idx) =>
            (idx === 0 ||
              idx === 3 ||
              idx === 6 ||
              idx === 9 ||
              idx === 12 ||
              idx === 14) && (
              <text
                key={idx}
                x={getX(idx)}
                y={chartHeight - 6}
                className="text-[8px] fill-slate-400"
                textAnchor="middle"
              >
                {d.month}
              </text>
            ),
        )}

        {/* Area fill */}
        <motion.path
          d={`${getSmoothPath(data.map((d) => d.pims))} L ${getX(data.length - 1)} ${yMax} L ${getX(0)} ${yMax} Z`}
          fill={color}
          fillOpacity={0.1}
          initial={{ opacity: 0 }}
          animate={{ opacity: drawProgress }}
          transition={{ duration: 1, delay: 0.3 }}
        />

        {/* Line */}
        <motion.path
          d={getSmoothPath(data.map((d) => d.pims))}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={pathLength}
          initial={{ strokeDashoffset: pathLength }}
          animate={{ strokeDashoffset: pathLength * (1 - drawProgress) }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
        />

        {/* End callout */}
        <AnimatePresence>
          {showCallouts && (
            <motion.g
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <circle
                cx={getX(data.length - 1)}
                cy={getY(endValue)}
                r="4"
                fill={color}
                stroke="white"
                strokeWidth="2"
              />
              <line
                x1={getX(data.length - 1) + 4}
                y1={getY(endValue)}
                x2={getX(data.length - 1) + 12}
                y2={getY(endValue)}
                stroke={color}
                strokeWidth="1.5"
              />
              <rect
                x={getX(data.length - 1) + badgeOffset}
                y={getY(endValue) - 11}
                width={badgeWidth}
                height={22}
                rx={11}
                fill={color}
              />
              <text
                x={getX(data.length - 1) + badgeOffset + 10}
                y={getY(endValue) + 4}
                className="text-[11px] font-bold"
                fill="white"
              >
                ↓{improvement}%
              </text>
              <text
                x={getX(data.length - 1) + badgeOffset + 46}
                y={getY(endValue) + 4}
                className="text-[10px]"
                fill="rgba(255,255,255,0.9)"
              >
                {label}
              </text>
            </motion.g>
          )}
        </AnimatePresence>
      </svg>
    </div>
  );
}

// ============================================================================
// HERO SLIDE
// ============================================================================

function HeroSlide({
  isActive,
  onNext,
  permissions,
}: {
  isActive: boolean;
  onNext: () => void;
  permissions: TrackPermissions;
}) {
  const reducedMotion = useReducedMotion();

  return (
    <div className="relative h-full flex items-center justify-center bg-gradient-to-br from-eventus-navy via-eventus-navy-dark to-eventus-purple/20 overflow-hidden">
      {/* Background pattern */}
      {!reducedMotion && (
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(135, 123, 233, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(135, 123, 233, 0.3) 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          />
        </div>
      )}

      <div className="relative z-10 text-center px-6 max-w-4xl">
        <motion.div
          initial={reducedMotion ? {} : { opacity: 0, y: 40 }}
          animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6"
          >
            <span className="text-eventus-teal/80 text-sm font-medium tracking-widest uppercase">
              Leader Support Guide
            </span>
          </motion.div>

          <motion.h1
            initial={reducedMotion ? {} : { opacity: 0, scale: 0.95 }}
            animate={
              isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }
            }
            transition={{
              duration: 0.6,
              delay: 0.3,
              type: "spring",
              stiffness: 100,
            }}
            className="text-6xl sm:text-7xl md:text-8xl font-bold mb-6"
          >
            <span className="text-white">EQuIP</span>
            <span className="text-white">
              {" "}
              2026
            </span>
            <span className="text-eventus-teal text-4xl sm:text-5xl md:text-6xl font-semibold align-top ml-2">
              BETA
            </span>
          </motion.h1>

          <motion.p
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed mb-8"
          >
            For Clinical Leadership — everything you need to
            support your clinicians through the 2026 program.
          </motion.p>

          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="flex flex-wrap gap-3 justify-center"
          >
            <button
              onClick={onNext}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-eventus-navy text-sm font-bold rounded-full transition-all hover:shadow-lg hover:-translate-y-0.5 border-2 border-white"
            >
              <ArrowDown className="w-4 h-4" />
              Get Started
            </button>
            {permissions.canAccessPhysician && (
              <Link
                to="/physician/framework"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/20 transition-all hover:border-white/40"
              >
                <Stethoscope className="w-4 h-4" />
                Physician Framework
              </Link>
            )}
            {permissions.canAccessAPC && (
              <Link
                to="/non-physician/framework"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/20 transition-all hover:border-white/40"
              >
                <Users className="w-4 h-4" />
                APC Framework
              </Link>
            )}
            {permissions.hasAnyAccess && (
              <Link
                to="/leadership"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/20 transition-all hover:border-white/40"
              >
                <BarChart3 className="w-4 h-4" />
                Leadership Dashboard
              </Link>
            )}
            {permissions.isAdmin && (
              <Link
                to="/admin/email-templates"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500/20 hover:bg-amber-500/30 backdrop-blur-sm text-amber-200 text-sm font-medium rounded-full border border-amber-400/30 transition-all hover:border-amber-400/50"
              >
                <Mail className="w-4 h-4" />
                Email Templates
              </Link>
            )}
          </motion.div>
        </motion.div>
      </div>

      <HeroDownArrow onClick={onNext} />
    </div>
  );
}

// ============================================================================
// SLIDE 1: WHY THIS PROGRAM EXISTS (REVISED)
// ============================================================================

function Slide1WhyExists({
  isActive,
  onNext,
}: {
  isActive: boolean;
  onNext: () => void;
}) {
  return (
    <div className="relative h-full flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-eventus-purple/5">
      <div className="max-w-5xl mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: The Core Purpose */}
          <div className="space-y-6">
            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0}
            >
              <p className="text-sm font-semibold text-eventus-purple uppercase tracking-wider mb-2">
                First, the Foundation
              </p>
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-eventus-navy leading-tight">
                Why does this program exist?
              </h2>
            </motion.div>

            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.15}
              className="space-y-4"
            >
              <p className="text-lg text-slate-600 leading-relaxed">
                Your role: help your clinicians understand what earns
                recognition. When they understand, your region's outcomes improve.
                Here's what drives the incentive pool:
              </p>
              <ul className="space-y-2 text-base text-slate-700">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-eventus-purple mt-2.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">
                      Better patient outcomes
                    </span>{" "}
                    — quality care that shows in the numbers
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-eventus-purple mt-2.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">
                      Consistent care delivery
                    </span>{" "}
                    — reliable, measurable, documented
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-eventus-purple mt-2.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Sustainable growth</span> —
                    more patients served well
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-eventus-purple mt-2.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">
                      Accurate documentation & coding
                    </span>{" "}
                    — including G2211 when appropriate
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Right: Your Job as a Leader + Key Message */}
          <div className="space-y-5">
            <motion.div
              variants={slideLeftVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.2}
              className="bg-gradient-to-br from-eventus-navy to-eventus-navy-dark rounded-xl p-6 text-white"
            >
              <h3 className="font-bold text-eventus-teal mb-4 text-lg">
                Your Job as a Leader
              </h3>
              <div className="space-y-3 text-sm">
                <p>
                  <span className="text-eventus-teal font-bold">EXPLAIN</span> —
                  Share and guide clinicians through the 6 principles of the program
                </p>
                <p>
                  <span className="text-eventus-teal font-bold">POINT</span> —
                  Direct clinicians to dashboards early for success visibility
                </p>
                <p>
                  <span className="text-eventus-teal font-bold">ESCALATE</span>{" "}
                  — Don't promise exceptions, direct program questions to{' '}
                  <a href="mailto:HR@eventuswh.com" className="underline">HR@eventuswh.com</a>
                </p>
              </div>
            </motion.div>

            <motion.div
              variants={slideLeftVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.35}
              className="bg-eventus-purple/10 rounded-xl p-5 border border-eventus-purple/20"
            >
              <p className="text-base font-medium leading-relaxed text-eventus-navy">
                When we achieve these together, the incentive pool grows. EQuIP
                rewards clinicians whose work directly contributes to this
                success.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
      <SlideDownArrow onClick={onNext} variant="dark" label="Continue" />
    </div>
  );
}

// ============================================================================
// SLIDE 2: OUTCOMES SUCCESS - Recognition Paid Out
// ============================================================================

function Slide2OutcomesSuccess({
  isActive,
  onNext,
}: {
  isActive: boolean;
  onNext: () => void;
}) {
  return (
    <div className="relative h-full flex items-center justify-center bg-gradient-to-br from-eventus-navy via-eventus-navy-dark to-eventus-purple/30 overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(107, 196, 214, 0.3) 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Narrative */}
          <div className="space-y-6">
            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0}
            >
              <p className="text-sm font-semibold text-eventus-teal uppercase tracking-wider mb-3">
                2025: Year One
              </p>
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-4">
                The incentive program <span className="text-eventus-teal">worked.</span>
              </h2>
            </motion.div>

            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.15}
              className="space-y-4"
            >
              <p className="text-xl text-white/90 leading-relaxed">
                EQuIP's first year delivered real results. Clinicians engaged
                with quality metrics, drove measurable improvements, and earned
                recognition for their work.
              </p>
              <p className="text-lg text-white/70 leading-relaxed">
                The incentive pool wasn't just allocated—it was{" "}
                <span className="text-eventus-teal font-semibold">earned</span>{" "}
                through documented, measurable impact.
              </p>
            </motion.div>

            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.3}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20"
            >
              <p className="text-white/90 text-base leading-relaxed">
                <span className="text-eventus-teal font-bold">"</span>
                When clinicians saw their outcomes metrics in real-time, behaviors
                changed. Documentation improved. Care gaps closed. The
                visibility alone drove progress.
                <span className="text-eventus-teal font-bold">"</span>
              </p>
            </motion.div>
          </div>

          {/* Right: Enhanced Recognition Card */}
          <motion.div
            variants={slideLeftVariants}
            initial="hidden"
            animate={isActive ? "visible" : "hidden"}
            custom={0.2}
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-eventus-teal/20 rounded-3xl blur-2xl" />

              {/* Main card */}
              <div className="relative bg-gradient-to-br from-white via-white to-slate-50 rounded-2xl p-8 shadow-2xl border border-white/50">
                {/* Header badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 rounded-full mb-6">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                    Year One Complete
                  </span>
                </div>

                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Total Recognition Paid Out
                </p>

                {/* Big number with animation */}
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-6xl lg:text-7xl font-black text-eventus-navy tracking-tight">
                    $
                    <AnimatedCounter
                      from={0}
                      to={940}
                      duration={1800}
                      isActive={isActive}
                    />
                    K
                  </span>
                </div>
                <p className="text-slate-500 text-sm mb-8">
                  distributed across four quarters in 2025
                </p>

                <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-6" />

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="text-3xl lg:text-4xl font-bold text-eventus-purple mb-1">
                      <AnimatedCounter
                        from={0}
                        to={250}
                        duration={1500}
                        isActive={isActive}
                      />
                    </div>
                    <p className="text-slate-600 text-xs font-medium">
                      clinicians earned awards
                      <br />
                      each quarter
                    </p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="text-3xl lg:text-4xl font-bold text-eventus-teal-dark mb-1">
                      $
                      <AnimatedCounter
                        from={0}
                        to={1042}
                        duration={1600}
                        isActive={isActive}
                      />
                    </div>
                    <p className="text-slate-600 text-xs font-medium">
                      average award
                      <br />
                      per qualifying clinician
                    </p>
                  </div>
                </div>

                {/* Decorative element */}
                <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-eventus-teal/10 rounded-full blur-xl" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <SlideDownArrow onClick={onNext} variant="light" label="See the data" />
    </div>
  );
}

// ============================================================================
// SLIDE 3: PIMs DATA - Operationalized Impact
// ============================================================================

function Slide3PimsData({
  isActive,
  onNext,
}: {
  isActive: boolean;
  onNext: () => void;
}) {
  return (
    <div className="relative h-full flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="max-w-6xl mx-auto px-6 w-full">
        <div className="space-y-6">
          {/* Header */}
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate={isActive ? "visible" : "hidden"}
            custom={0}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-eventus-navy leading-tight mb-4">
              We operationalized PIMs data for the first time.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Potentially inappropriate medications—tracked, measured, and
              visible across the organization.{" "}
              <span className="font-semibold text-eventus-navy">
                When clinicians could see it, they started changing it.
              </span>
            </p>
          </motion.div>

          {/* Charts */}
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate={isActive ? "visible" : "hidden"}
            custom={0.2}
            className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 max-w-4xl mx-auto"
          >
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-emerald-500" />
              <span className="text-base font-semibold text-slate-700">
                PIMs Impact: Oct 2024 → Dec 2025
              </span>
            </div>
            <OutcomesChart isActive={isActive} />
          </motion.div>

          {/* Conclusion callout */}
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate={isActive ? "visible" : "hidden"}
            custom={0.4}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-eventus-navy rounded-xl p-6 text-center shadow-lg">
              <p className="text-xl lg:text-2xl font-bold text-white leading-relaxed">
                Great outcomes.{" "}
                <span className="text-eventus-teal">
                  Now with clearer recognition.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      <SlideDownArrow onClick={onNext} variant="dark" label="Continue" />
    </div>
  );
}

// ============================================================================
// SLIDE 3: INCORPORATING YOUR FEEDBACK
// ============================================================================

function Slide3TheProblem({
  isActive,
  onNext,
}: {
  isActive: boolean;
  onNext: () => void;
}) {
  return (
    <div className="relative h-full flex items-center justify-center bg-slate-100">
      <div className="max-w-6xl mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left: Context */}
          <div className="space-y-6">
            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0}
            >
              <p className="text-sm font-semibold text-eventus-teal uppercase tracking-wider mb-2">
                Building on What Works
              </p>
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-eventus-navy leading-tight">
                We listened. We improved.
              </h2>
            </motion.div>

            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.2}
              className="space-y-4"
            >
              <p className="text-lg text-slate-600 leading-relaxed">
                Clinicians told us they wanted{" "}
                <span className="font-semibold text-eventus-navy">
                  more clarity, more transparency, and more ways to succeed
                </span>
                . EQuIP 2026 BETA incorporates that feedback. You told us you needed
                more support as leaders and this guide is your key to success in
                helping clinicians understand the 2026 program.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                The 2026 structure is designed to recognize strong performance
                across multiple dimensions and give you more support to help
                your teams understand the incentive program.
              </p>
            </motion.div>

            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.4}
              className="bg-white rounded-xl p-5 border-l-4 border-eventus-teal shadow-sm"
            >
              <p className="text-lg text-eventus-navy font-medium leading-relaxed">
                Performance across multiple areas{" "}
                <span className="font-bold">supports your overall success</span>
                . Strong results in one measure contribute to your total incentive.
              </p>
            </motion.div>
          </div>

          {/* Right: What Changed */}
          <div className="space-y-6">
            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.3}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200"
            >
              <h3 className="font-bold text-eventus-navy mb-4 text-lg">
                What We Heard & How We Responded
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-eventus-teal flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-eventus-navy">
                      "We want to see where we stand"
                    </span>
                    <p className="text-sm text-slate-600 mt-1">
                      → Real-time dashboards with performance visibility
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-eventus-teal flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-eventus-navy">
                      "We want multiple ways to earn"
                    </span>
                    <p className="text-sm text-slate-600 mt-1">
                      → Each incentive measure functions independently
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-eventus-teal flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-eventus-navy">
                      "We want fairness and consistency"
                    </span>
                    <p className="text-sm text-slate-600 mt-1">
                      → Same thresholds applied uniformly across all roles
                    </p>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate={isActive ? "visible" : "hidden"}
              custom={0.5}
              className="bg-eventus-navy rounded-xl p-5 text-white"
            >
              <p className="text-base font-medium leading-relaxed">
                EQuIP 2026 BETA is aligned with our company goals and designed to
                reward the meaningful clinical work clinicians are already doing.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
      <SlideDownArrow onClick={onNext} variant="dark" label="Continue" />
    </div>
  );
}

// ============================================================================
// SLIDE 5: CLOSE
// ============================================================================

function Slide5Close({
  isActive,
  permissions,
}: {
  isActive: boolean;
  permissions: TrackPermissions;
}) {
  const reducedMotion = useReducedMotion();

  return (
    <div className="relative h-full flex items-center bg-gradient-to-br from-eventus-purple via-eventus-navy to-eventus-navy-dark overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      <div className="relative w-full max-w-4xl mx-auto px-6 py-12 text-center">
        {/* Progressive reveals */}
        <div className="space-y-3 mb-10">
          <motion.p
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl lg:text-2xl text-white/80"
          >
            Strong performance recognized. Every measure counts.
          </motion.p>
          <motion.p
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-xl lg:text-2xl text-white font-semibold"
          >
            Each measure counts. Your results add up.
          </motion.p>
        </div>

        {/* Title */}
        <motion.h1
          initial={reducedMotion ? {} : { opacity: 0, scale: 0.95 }}
          animate={
            isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }
          }
          transition={{
            duration: 0.6,
            delay: 0.55,
            type: "spring",
            stiffness: 120,
          }}
          className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6"
        >
          <span className="text-white">EQuIP</span>
          <span className="text-white">
            {" "}
            2026
          </span>
          <span className="text-eventus-teal text-3xl sm:text-4xl md:text-5xl font-semibold align-top ml-2">
            BETA
          </span>
        </motion.h1>

        {/* CTA links */}
        <motion.div
          initial={reducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="flex flex-wrap gap-4 justify-center"
        >
          {permissions.canAccessPhysician && (
            <Link
              to="/physician/framework"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-eventus-teal hover:bg-eventus-teal-light text-eventus-navy font-bold rounded-full transition-all hover:shadow-2xl hover:-translate-y-1"
            >
              <Stethoscope className="w-5 h-5" />
              <span className="text-lg">Physician Framework</span>
            </Link>
          )}
          {permissions.canAccessAPC && (
            <Link
              to="/non-physician/framework"
              className={`group inline-flex items-center gap-3 px-8 py-4 ${
                permissions.canAccessPhysician
                  ? "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  : "bg-eventus-teal hover:bg-eventus-teal-light text-eventus-navy"
              } backdrop-blur-sm font-bold rounded-full transition-all hover:shadow-xl hover:-translate-y-1`}
            >
              <Users className="w-5 h-5" />
              <span className="text-lg">APC Framework</span>
            </Link>
          )}
          {permissions.hasAnyAccess && (
            <Link
              to="/leadership"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm font-bold rounded-full transition-all hover:shadow-xl hover:-translate-y-1"
            >
              <BarChart3 className="w-5 h-5" />
              <span className="text-lg">Leadership Dashboard</span>
            </Link>
          )}
          {permissions.canAccessPhysician && (
            <Link
              to="/physician/leader-guide"
              className="group inline-flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm font-semibold rounded-full transition-all hover:shadow-xl hover:-translate-y-1"
            >
              <BookOpen className="w-4 h-4" />
              <span>Leader Guide</span>
            </Link>
          )}
          {!permissions.canAccessPhysician && permissions.canAccessAPC && (
            <Link
              to="/non-physician/leader-guide"
              className="group inline-flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm font-semibold rounded-full transition-all hover:shadow-xl hover:-translate-y-1"
            >
              <BookOpen className="w-4 h-4" />
              <span>Leader Guide</span>
            </Link>
          )}
          {permissions.canAccessPhysician && (
            <Link
              to="/physician/faq"
              className="group inline-flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm font-semibold rounded-full transition-all hover:shadow-xl hover:-translate-y-1"
            >
              <MessageSquare className="w-4 h-4" />
              <span>FAQ</span>
            </Link>
          )}
          {!permissions.canAccessPhysician && permissions.canAccessAPC && (
            <Link
              to="/non-physician/faq"
              className="group inline-flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm font-semibold rounded-full transition-all hover:shadow-xl hover:-translate-y-1"
            >
              <MessageSquare className="w-4 h-4" />
              <span>FAQ</span>
            </Link>
          )}
          {permissions.isAdmin && (
            <Link
              to="/admin/email-templates"
              className="group inline-flex items-center gap-3 px-6 py-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-400/30 backdrop-blur-sm font-semibold rounded-full transition-all hover:shadow-xl hover:-translate-y-1"
            >
              <Mail className="w-4 h-4" />
              <span>Email Templates</span>
            </Link>
          )}
        </motion.div>
      </div>
    </div>
  );
}

// ============================================================================
// MOBILE DETECTION HOOK
// ============================================================================

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
}

// ============================================================================
// MAIN LANDING PAGE
// ============================================================================

// No Access Component - shown when user has no EQuIP roles
function NoAccessState() {
  const { user, logout } = useAuth();
  const reducedMotion = useReducedMotion();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-eventus-navy via-eventus-navy-dark to-eventus-purple/20 p-6">
      <motion.div
        initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full text-center"
      >
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
          <motion.div
            initial={reducedMotion ? {} : { scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-6"
          >
            <ShieldAlert className="w-8 h-8 text-amber-400" />
          </motion.div>

          <h1 className="text-4xl font-bold mb-2">
            <span className="text-white">EQuIP</span>
            <span className="bg-gradient-to-r from-eventus-teal to-eventus-teal-light bg-clip-text text-transparent">
              {" "}
              2026
            </span>
          </h1>

          <p className="text-white/70 mb-6">
            {user?.name ? (
              <>
                Hi{" "}
                <span className="text-white font-medium">
                  {user.name.split(" ")[0]}
                </span>
                , you don't have access to the EQuIP program yet.
              </>
            ) : (
              "You don't have access to the EQuIP program yet."
            )}
          </p>

          {/* Required roles info */}
          <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
            <p className="text-white/60 text-sm mb-2">An EQuIP role is required to access this program:</p>
            <div className="flex flex-wrap gap-2">
              {['equip.admin', 'equip.read', 'equip.read-only', 'equip.css'].map((role) => (
                <span
                  key={role}
                  className="px-2 py-1 bg-white/10 rounded text-xs text-white/80 font-mono"
                >
                  {role}
                </span>
              ))}
            </div>
          </div>

          <p className="text-white/50 text-sm mb-8">
            If you believe this is an error,{" "}
            <a
              href="mailto:clinicalquality@eventuswh.com?subject=EQuIP%20Access%20Request"
              className="text-eventus-teal hover:text-eventus-teal-light underline transition-colors"
            >
              contact your administrator
            </a>
            .
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:clinicalquality@eventuswh.com?subject=EQuIP%202026%20Access%20Request"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-eventus-teal hover:bg-eventus-teal-light text-eventus-navy font-semibold rounded-full transition-all hover:shadow-lg"
            >
              <Mail className="w-4 h-4" />
              Contact Admin
            </a>
            <button
              onClick={handleLogout}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-full border border-white/20 transition-all"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}


export function Landing() {
  const isMobile = useIsMobile();

  // Check user permissions (use effectiveRoles so impersonation works correctly)
  const { hasAnyRole, isAdmin, roles } = useRBAC();

  // equip.read-only users go to their participant landing
  if (isEquipReadOnly(roles)) {
    return <Navigate to="/user" replace />;
  }

  // CSS-only users go straight to performance dashboard
  if (isEquipCSSOnly(roles)) {
    return <Navigate to="/performance" replace />;
  }

  const permissions: TrackPermissions = {
    canAccessPhysician: hasAnyRole(...PHYSICIAN_TRACK_ROLES),
    canAccessAPC: hasAnyRole(...APC_TRACK_ROLES),
    hasAnyAccess:
      hasAnyRole(...PHYSICIAN_TRACK_ROLES) || hasAnyRole(...APC_TRACK_ROLES),
    isAdmin,
  };

  if (!permissions.hasAnyAccess) {
    return <NoAccessState />;
  }

  return isMobile ? (
    <MobileLanding permissions={permissions} />
  ) : (
    <DesktopLanding permissions={permissions} />
  );
}

// ============================================================================
// MOBILE LANDING - Traditional scrolling layout
// ============================================================================

function MobileLanding({ permissions }: { permissions: TrackPermissions }) {
  const noop = () => {};

  return (
    <div className="bg-eventus-navy min-h-screen pb-20">
      <FloatingNav
        items={[
          ...((permissions.canAccessPhysician || permissions.canAccessAPC)
            ? [{
                id: "physician-group",
                label: "Leadership",
                path: "",
                icon: <Stethoscope className="w-4 h-4" />,
                children: [
                  ...(permissions.canAccessPhysician ? [{ id: "physician-lead", label: "Physician Lead", path: "/physician/framework", icon: <Stethoscope className="w-4 h-4" /> }] : []),
                  ...(permissions.canAccessAPC ? [{ id: "apc-lead", label: "APC Lead", path: "/non-physician/framework", icon: <Users className="w-4 h-4" /> }] : []),
                ],
              }]
            : []),
          ...(permissions.isAdmin
            ? [{
                id: "equip-user",
                label: "EQuIP User",
                path: "/framework",
                icon: <BarChart3 className="w-4 h-4" />,
              }]
            : []),
          ...((permissions.hasAnyAccess || permissions.isAdmin)
            ? [{
                id: "dashboard-group",
                label: "Dashboard",
                path: "",
                icon: <BarChart3 className="w-4 h-4" />,
                children: [
                  ...(permissions.hasAnyAccess ? [{ id: "leadership", label: "Leadership", path: "/leadership", icon: <BarChart3 className="w-4 h-4" /> }] : []),
                  ...(permissions.isAdmin ? [{ id: "clinician-dashboard", label: "Clinician", path: "/performance", icon: <BarChart3 className="w-4 h-4" /> }] : []),
                  ...(permissions.isAdmin ? [{ id: "sup-physician", label: "Sup. Physician", path: "/apc-dashboard", icon: <Stethoscope className="w-4 h-4" /> }] : []),
                  ...(!permissions.isAdmin && permissions.canAccessPhysician ? [{ id: "my-apcs", label: "My APCs", path: "/apc-dashboard", icon: <Stethoscope className="w-4 h-4" /> }] : []),
                ],
              }]
            : []),
          ...(permissions.isAdmin
            ? [{
                id: "templates",
                label: "Templates",
                path: "/admin/email-templates",
                icon: <Mail className="w-4 h-4" />,
              }]
            : []),
        ]}
        showHome={false}
      />

      {/* All sections rendered vertically with natural height */}
      <div className="min-h-screen flex items-center justify-center">
        <HeroSlide isActive={true} onNext={noop} permissions={permissions} />
      </div>
      <Slide2OutcomesSuccess isActive={true} onNext={noop} />
      <Slide3PimsData isActive={true} onNext={noop} />
      <Slide3TheProblem isActive={true} onNext={noop} />
      <Slide1WhyExists isActive={true} onNext={noop} />
      <Slide5Close isActive={true} permissions={permissions} />
    </div>
  );
}

// ============================================================================
// DESKTOP LANDING - Slide deck with keyboard/wheel/dot navigation
// ============================================================================

function DesktopLanding({ permissions }: { permissions: TrackPermissions }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const goToSlide = useCallback(
    (index: number) => {
      if (isAnimating || index < 0 || index >= TOTAL_SLIDES) return;
      setIsAnimating(true);
      setCurrentSlide(index);
      setTimeout(() => setIsAnimating(false), 600);
    },
    [isAnimating],
  );

  const goNext = useCallback(
    () => goToSlide(currentSlide + 1),
    [currentSlide, goToSlide],
  );
  const goPrev = useCallback(
    () => goToSlide(currentSlide - 1),
    [currentSlide, goToSlide],
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Home") {
        e.preventDefault();
        goToSlide(0);
      } else if (e.key === "End") {
        e.preventDefault();
        goToSlide(TOTAL_SLIDES - 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev, goToSlide]);

  // Wheel navigation
  useEffect(() => {
    let lastScrollTime = 0;
    const scrollThreshold = 800;

    const handleWheel = (e: WheelEvent) => {
      const now = Date.now();
      if (now - lastScrollTime < scrollThreshold) return;

      if (Math.abs(e.deltaY) > 50) {
        lastScrollTime = now;
        if (e.deltaY > 0) {
          goNext();
        } else {
          goPrev();
        }
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [goNext, goPrev]);

  const slides = [
    <HeroSlide
      key="hero"
      isActive={currentSlide === 0}
      onNext={goNext}
      permissions={permissions}
    />,
    <Slide2OutcomesSuccess
      key="slide1"
      isActive={currentSlide === 1}
      onNext={goNext}
    />,
    <Slide3PimsData
      key="slide2"
      isActive={currentSlide === 2}
      onNext={goNext}
    />,
    <Slide3TheProblem
      key="slide3"
      isActive={currentSlide === 3}
      onNext={goNext}
    />,
    <Slide1WhyExists
      key="slide4"
      isActive={currentSlide === 4}
      onNext={goNext}
    />,
    <Slide5Close
      key="slide5"
      isActive={currentSlide === 5}
      permissions={permissions}
    />,
  ];

  return (
    <div
      ref={containerRef}
      className="bg-eventus-navy h-screen overflow-hidden"
    >
      <FloatingNav
        items={[
          ...((permissions.canAccessPhysician || permissions.canAccessAPC)
            ? [{
                id: "physician-group",
                label: "Leadership",
                path: "",
                icon: <Stethoscope className="w-4 h-4" />,
                children: [
                  ...(permissions.canAccessPhysician ? [{ id: "physician-lead", label: "Physician Lead", path: "/physician/framework", icon: <Stethoscope className="w-4 h-4" /> }] : []),
                  ...(permissions.canAccessAPC ? [{ id: "apc-lead", label: "APC Lead", path: "/non-physician/framework", icon: <Users className="w-4 h-4" /> }] : []),
                ],
              }]
            : []),
          ...(permissions.isAdmin
            ? [{
                id: "equip-user",
                label: "EQuIP User",
                path: "/framework",
                icon: <BarChart3 className="w-4 h-4" />,
              }]
            : []),
          ...((permissions.hasAnyAccess || permissions.isAdmin)
            ? [{
                id: "dashboard-group",
                label: "Dashboard",
                path: "",
                icon: <BarChart3 className="w-4 h-4" />,
                children: [
                  ...(permissions.hasAnyAccess ? [{ id: "leadership", label: "Leadership", path: "/leadership", icon: <BarChart3 className="w-4 h-4" /> }] : []),
                  ...(permissions.isAdmin ? [{ id: "clinician-dashboard", label: "Clinician", path: "/performance", icon: <BarChart3 className="w-4 h-4" /> }] : []),
                  ...(permissions.isAdmin ? [{ id: "sup-physician", label: "Sup. Physician", path: "/apc-dashboard", icon: <Stethoscope className="w-4 h-4" /> }] : []),
                  ...(!permissions.isAdmin && permissions.canAccessPhysician ? [{ id: "my-apcs", label: "My APCs", path: "/apc-dashboard", icon: <Stethoscope className="w-4 h-4" /> }] : []),
                ],
              }]
            : []),
          ...(permissions.isAdmin
            ? [{
                id: "templates",
                label: "Templates",
                path: "/admin/email-templates",
                icon: <Mail className="w-4 h-4" />,
              }]
            : []),
        ]}
        showHome={false}
        forceVisible={currentSlide === 0}
      />

      <SlideNavigation
        currentSlide={currentSlide}
        totalSlides={TOTAL_SLIDES}
        onNext={goNext}
        onPrev={goPrev}
        onJumpTo={goToSlide}
        isAnimating={isAnimating}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="h-screen w-full"
        >
          {slides[currentSlide]}
        </motion.div>
      </AnimatePresence>

      <div className="fixed bottom-4 left-4 z-50 bg-eventus-navy/80 backdrop-blur-sm text-white/80 text-sm font-mono px-3 py-1.5 rounded-full border border-white/10">
        {currentSlide + 1} / {TOTAL_SLIDES}
      </div>
    </div>
  );
}
